// React imports
import { useState, useEffect, useMemo } from 'react';

// General imports
import _ from 'lodash';
import axios from 'axios';
import validator from 'email-validator';

// Fontawesome imports
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faDownload, faEnvelope, faLink, faBuilding, faClipboard, faTimes, faRotateRight, faCheck, faChevronDown, faEye } from '@fortawesome/free-solid-svg-icons';
import { green, red, amber } from '@mui/material/colors';

// MUI imports
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Alert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Avatar from '@mui/material/Avatar';
import Snackbar from '@mui/material/Snackbar';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import LoadingButton from '@mui/lab/LoadingButton';
import CircularProgress from '@mui/material/CircularProgress';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import DialogContentText from '@mui/material/DialogContentText';
import Tooltip from '@mui/material/Tooltip';
import { useTheme, alpha } from '@mui/material/styles';

// Local imports
import CWXml from '../../CWXml.js';
import SendOpportunityReplyDialog from '../components/SendOpportunityReplyDialog.js';
import SendOpportunityDialog from '../components/SendOpportunityDialog.js';
import getBaseUrl from '../../GetBaseUrl.js';
import { useQuotePdfDownload, QuotePdfNotice } from '../components/QuotePdfDownload.js';
import { statusToText } from '../../OpportunityRow.utils.js';
import readableId from '../../ReadableId.js';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import PickOffersAndMarkWonDialog from '../components/PickOffersAndMarkWonDialog.js';
import LostReasonDialog from '../components/LostReasonDialog.js';
import { faTractor, faTriangleExclamation, faLock, faLockOpen } from '@fortawesome/pro-light-svg-icons';
import email from '../utils/email.js';
import { calcOfferSellAndCost } from '../../utils/opportunityFinancials.js';
import currencyToText from '../../CurrencyToText.js';

dayjs.extend(advancedFormat);

function OpportunityPreviewAndSendBase(props) {
    const { org, type, opportunity, me, accounts, accesstoken, organization, minimized,
        onUpdateOpportunity, owner, suppliers, seaPorts, tags, setTags, children,
        exchangeRates, emailBody, emailLoginInfo, onEmailRefreshConnection, onEmailLogout, unlocode,
        lockedExchangeRates, onLockExchangeRates, attachPDF, setAttachPDF,
    } = props;
    const [ screen, setScreen ] = useState(null);
    const [ message, setMessage ] = useState(null);
    const [ exportCWDialogOpen, setExportCWDialogOpen ] = useState(false);
    // Was a Print action that opened the offer page with print=true - a browser print view,
    // not a file. This downloads the same PDF the send attaches, built from an end-customer
    // token so it carries no PREVIEW watermark.
    const { downloadingPdf, pdfError, downloadPdf: onDownloadPdf, notice: pdfNotice, dismissNotice: dismissPdfNotice } =
        useQuotePdfDownload({ opportunity, accesstoken, minimized });
    const [ cargowiseHistory, setCargowiseHistory ] = useState(null);
    const [ sendingCargowise, setSendingCargowise ] = useState(false);
    const [ previewUrl, setPreviewUrl ] = useState(null);
    // const [ pdfData, setPdfData ] = useState(null); // { blobUrl, filename } or 'failed' — disabled for now
    const theme = useTheme();

    useEffect(() => {
        if (opportunity.status === 'deleted') return;
        (async () => {
            try {   
                const cargowiseSettled = await axios(`/api/opportunities/${opportunity.id}/cargowise-status?accesstoken=${accesstoken}&organizationId=${organization.id}`)
                setCargowiseHistory(cargowiseSettled.data);
            } catch (e) {
                if (e.response?.status === 404) {
                    setCargowiseHistory({ status: 'NOT_FOUND' });
                }
            }
        })();
    }, [ opportunity, accesstoken ]);

    useEffect(() => {
        if (opportunity.status === 'deleted') return;
        (async () => {
            try {
                const preview = await axios(`/api/opportunities/${opportunity.id}/offer-url?accesstoken=${accesstoken}&organizationId=${organization.id}&type=preview&minimized=${minimized ? 'true' : 'false'}`);
                setPreviewUrl(preview.data);
            } catch (error) {
                console.error(error);
            }
        })();
    }, [ opportunity, accesstoken, minimized ]);

    // Lock warning disabled — exchange rates are now auto-locked on send
    const showLockWarning = false;

    function onNewOpportunity({ opportunity }) {
        onUpdateOpportunity({ opportunity });
    }

    if (opportunity.status === 'deleted') {
        return (
            <Box sx={{ background: alpha(theme.palette.text.primary, 0.01) }}>
                <Box sx={{ position: 'relative', paddingBottom: '36px' }}>
                    <Box sx={{ padding: '0px 12px' }}>
                        <Typography variant='body1' sx={{ fontSize: '16px', paddingTop: '12px' }} component={Box}>
                            Since this opportunity has been deleted, you cannot preview or send it.
                        </Typography>
                    </Box>
                </Box>
            </Box>
        )
    }

    // Append a system entry to the opportunity's internal Updates & Comments timeline
    // recording that it was sent to TMS, with a timestamp. Sequenced after the tag write so
    // the two field-level PUTs don't clobber each other.
    async function logSentToTms() {
        try {
            // Only schema-allowed update fields (created / createdBy / html): the updates schema is
            // strict().noUnknown(), so an extra key such as `system` 400s the PUT and the entry is
            // silently dropped by the catch below.
            const activity = {
                created: new Date().getTime(),
                createdBy: me?.email,
                html: `Sent to TMS (CargoWise) on ${dayjs().format('MMM D, YYYY h:mm A')}${me?.email ? ` by ${me.email}` : ''}.`,
            };
            const updates = [...(opportunity.updates || []), activity];
            const { data: _opportunity } = await axios.put(`/api/opportunities/${opportunity.id}?accesstoken=${accesstoken}&organizationId=${organization.id}`, { opportunity: { updates } });
            onUpdateOpportunity({ opportunity: _opportunity });
        } catch (e) {
            console.error('Failed to log Sent to TMS activity', e);
        }
    }

    async function onSendToCargoWise({ structure, text }) {
        setExportCWDialogOpen(false);
        setSendingCargowise(true);
        try {
            const { data } = await axios.post(`/api/opportunities/${opportunity.id}/send-to-cargowise?accesstoken=${accesstoken}`, { structure });
            setCargowiseHistory(data);
            await setTags({ tags: _.uniqWith([...tags || [], { label: 'Sent to TMS', color: 'blue' }], _.isEqual)});
            await logSentToTms();
        } catch (e) {
            setMessage({ open: true, severity: 'error', message: 'Error sending to CargoWise' });
        }

        if (organization.integrations.cargowise.method !== 'ftp' && organization.integrations.cargowise.method !== 'rest') {
            setTags({ tags: _.uniqWith([...tags || [], { label: 'Sent to TMS', color: 'blue' }], _.isEqual)});
            const win = window.open('', 'Title', 'toolbar=no,location=no,directories=no,status=no,menubar=no,scrollbars=yes,resizable=yes');
            win.document.body.innerText = text;
        }

        setSendingCargowise(false);
    }

    function showCargowiseResponse() {
        const win = window.open('', 'Title', 'toolbar=no,location=no,directories=no,status=no,menubar=no,scrollbars=yes,resizable=yes');
        win.document.body.innerHTML = '<pre>' + JSON.stringify(cargowiseHistory.response, null, 4).replace(/\\n/g, '\n') + '</pre>';
    }

    async function refreshCargowise() {
        setCargowiseHistory(null);

        const [cargowiseSettled] = await Promise.allSettled([
            axios(`/api/opportunities/${opportunity.id}/cargowise-status?accesstoken=${accesstoken}&organizationId=${organization.id}`)
        ]);

        if (cargowiseSettled.status === 'fulfilled') {
            setCargowiseHistory(cargowiseSettled.value.data);
        } else if (cargowiseSettled.reason.response?.status === 404) {
            setCargowiseHistory({ status: 'NOT_FOUND' });
        }
    }

    return (
        <Box>
            <Box sx={{ background: 'transparent', padding: '6px 24px', borderBottom: '1px solid rgba(59, 130, 246, 0.3)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <Typography variant='body1' sx={{ fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#334155' }}>
                        Shipment Details
                    </Typography>
                    <Typography variant='caption' component='div' sx={{ fontSize: '11px', color: theme.palette.text.secondary, display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                        Opportunity ID <b>{readableId(opportunity)}</b> · Created by <b>{opportunity.createdBy}</b> · Owner is <b>{opportunity.owner}</b> · Created on <b>{dayjs(opportunity.created).format('MMMM Do YYYY, h:mm A')}</b>
                        · <StatusMenu
                            opportunity={opportunity}
                            disabled={opportunity.status === 'deleted' || (me.email !== owner && me.email !== 'admin@monada.ai')}
                            organization={organization}
                            accesstoken={accesstoken}
                            onNewOpportunity={onNewOpportunity}
                            setMessage={setMessage}
                        />
                    </Typography>
                </Box>
            </Box>
            <FinancialSummaryBar opportunity={opportunity} exchangeRates={exchangeRates} organization={organization} lockedExchangeRates={lockedExchangeRates} />
            {(() => {
                const cur = organization.defaults?.currency || 'USD';
                const displayCurrencies = new Set();
                for (const offer of opportunity.offers || []) {
                    const offerDc = offer.displayCurrency?.to || offer.displayCurrency;
                    if (offerDc && offerDc.length === 3) displayCurrencies.add(offerDc);
                    for (const section of offer.sections || []) {
                        const dc = section.displayCurrency?.to || section.displayCurrency;
                        if (dc && dc.length === 3) displayCurrencies.add(dc);
                        for (const so of section.offers || []) {
                            for (const field of so.fields || []) {
                                const fdc = field.displayCurrency?.to || field.displayCurrency;
                                if (fdc && fdc.length === 3) displayCurrencies.add(fdc);
                            }
                        }
                    }
                }
                const hasDisplay = displayCurrencies.size > 0;
                const hasLocked = lockedExchangeRates?.live && Object.keys(lockedExchangeRates.live).length > 0;
                const isDraft = !opportunity.status || opportunity.status === 'draft' || opportunity.status === 'pre-draft';
                if (!hasDisplay) return null;
                const dcList = [...displayCurrencies].join(', ');
                const lockedRateLines = hasLocked ? [...displayCurrencies].map(dc => {
                    const rate = lockedExchangeRates.live[cur] / lockedExchangeRates.live[dc];
                    return rate ? `1 ${dc} = ${rate.toFixed(4)} ${cur}` : null;
                }).filter(Boolean) : [];
                return (
                    <Box sx={{ px: '24px', py: '4px', backgroundColor: hasLocked ? '#f0fdf4' : '#fffbeb', borderBottom: '1px solid #e2e8f0' }}>
                        <Typography sx={{ fontSize: '11px', color: hasLocked ? green[700] : amber[800], display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <FontAwesomeIcon icon={hasLocked ? faLock : faLockOpen} style={{ fontSize: '10px' }} />
                            {hasLocked
                                ? `Quote displayed in ${dcList} · Exchange rates locked on ${dayjs(lockedExchangeRates.locked).format('MMM D, YYYY [at] h:mm A')}${lockedRateLines.length > 0 ? ` (${lockedRateLines.join(', ')})` : ''}`
                                : isDraft
                                    ? `Quote will be displayed in ${dcList} using today's exchange rates · Rates will lock automatically when sent`
                                    : `Quote displayed in ${dcList} · Exchange rates were not locked when sent`}
                        </Typography>
                    </Box>
                );
            })()}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '4px', justifyContent: 'flex-end', padding: '8px 24px', mb: 2 }}>
                    {_.compact([{
                                title: 'Send by Email',
                                icon: faEnvelope,
                                disabled: !!opportunity.status || me.email !== owner,
                                onClick: () => setScreen('preview-enduser'),
                            }, type === 'one-off-email' ? null : {
                                title: 'Generate Link',
                                icon: faLink,
                                disabled: me.email !== owner,
                                onClick: () => setScreen('preview-link'),
                            }, type !== 'one-off-email' && previewUrl ? {
                                title: pdfError ? 'PDF failed' : downloadingPdf ? 'Preparing PDF…' : 'Download PDF',
                                icon: faDownload,
                                thinking: downloadingPdf,
                                disabled: opportunity.status === 'deleted' || me.email !== owner || downloadingPdf,
                                onClick: onDownloadPdf,
                            } : null, organization.integrations?.cargowise?.enabled && opportunity.type !== 'contract' ? {
                                title: cargowiseHistory?.status === 'PENDING'
                                    ? 'Sent to Cargowise'
                                    : cargowiseHistory?.status === 'ERROR'
                                        ? 'Error sending to CargoWise'
                                        : cargowiseHistory?.status === 'SUCCESS'
                                            ? 'Sent to CargoWise'
                                            : 'Send to CargoWise',
                                icon: cargowiseHistory?.status === 'ERROR' ? faTimes : cargowiseHistory?.status === 'SUCCESS' ? faCheck : '/cargowise.png',
                                onClick: () => setExportCWDialogOpen(true),
                                thinking: !cargowiseHistory || sendingCargowise,
                                disabled: cargowiseHistory?.status === 'PENDING' || cargowiseHistory?.status === 'ERROR' || cargowiseHistory?.status === 'SUCCESS' || !cargowiseHistory || sendingCargowise,
                                caption: sendingCargowise ? null
                                    : cargowiseHistory?.status === 'PENDING'
                                        ? <Typography variant='caption' sx={{ opacity: 0.6 }}>Waiting for update <FontAwesomeIcon style={{ cursor: 'pointer' }} onClick={refreshCargowise} icon={faRotateRight} /></Typography>
                                        : cargowiseHistory?.status === 'ERROR'
                                            ? <Typography variant='caption' color='error'>See response <span onClick={showCargowiseResponse} style={{ fontWeight: 800, textDecoration: 'underline', cursor: 'pointer' }}>here</span>, or try to <span onClick={() => setExportCWDialogOpen(true)} style={{ fontWeight: 800, textDecoration: 'underline', cursor: 'pointer' }}>resend</span>.</Typography>
                                            : cargowiseHistory?.status === 'SUCCESS' && !_.isEmpty(cargowiseHistory?.response) && typeof cargowiseHistory?.response === 'string'
                                                ? <Typography variant='caption' sx={{ color: green[500], textAlign: 'center' }}>EDI Interchange Id: <b>{JSON.parse(cargowiseHistory?.response)[0]}</b>.<br />See full response <span onClick={showCargowiseResponse} style={{ fontWeight: 800, textDecoration: 'underline', cursor: 'pointer' }}>here</span>.</Typography>
                                            : cargowiseHistory?.status === 'SUCCESS' && !_.isEmpty(cargowiseHistory?.response)
                                                ? <Typography variant='caption' sx={{ color: green[500], textAlign: 'center' }}>Cargowise Id: <b>{_.get(cargowiseHistory?.response, 'UniversalResponse.Data.UniversalEvent.Event.DataContext.DataSourceCollection.DataSource.Key')}</b>.<br />See full response <span onClick={showCargowiseResponse} style={{ fontWeight: 800, textDecoration: 'underline', cursor: 'pointer' }}>here</span>.</Typography>
                                                : cargowiseHistory?.status === 'SUCCESS' && _.isEmpty(cargowiseHistory?.response)
                                                    ? <Typography variant='caption'><span onClick={() => setExportCWDialogOpen(true)} style={{ color: green[500], textDecoration: 'underline', cursor: 'pointer' }}>Send again</span></Typography>
                                                    : null,
                            } : null]).map(but => (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <Tooltip key={but.title} title={but.title}>
                                        <span>
                                            <Button 
                                                disabled={but.disabled} 
                                                variant='outlined' 
                                                color='primary' 
                                                onClick={but.onClick} 
                                                sx={{ 
                                                    margin: '0px 2px', 
                                                    minWidth: '36px',
                                                    width: '36px',
                                                    height: '36px',
                                                    padding: '0px',
                                                    fontSize: '12px',
                                                    fontWeight: 400,
                                                    textTransform: 'none',
                                                    borderWidth: '1px',
                                                    '&:hover': {
                                                        borderWidth: '1px',
                                                    },
                                                    '& .MuiSvgIcon-root': {
                                                        fontSize: '12px',
                                                    },
                                                }}
                                            >
                                                {but.thinking && <CircularProgress size={14} />}
                                                {!but.thinking && (
                                                    // A button may carry a brand logo rather than a glyph, in
                                                    // which case its icon is a path to an image.
                                                    typeof but.icon === 'string'
                                                        ? <img src={but.icon} alt='' style={{ width: '16px', height: '16px', borderRadius: '3px', display: 'block' }} />
                                                        : <FontAwesomeIcon icon={but.icon} style={{ fontSize: '14px' }} />
                                                )}
                                            </Button>
                                        </span>
                                    </Tooltip>
                                    {but.caption && (
                                        <Typography variant='caption' sx={{ fontSize: '12px', fontWeight: 400, textTransform: 'none', color: theme.palette.text.secondary }}>
                                            {but.caption}
                                        </Typography>
                                    )}
                                </Box>
                            ))}
                </Box>
            {children}
            {opportunity.status !== 'deleted' && type !== 'one-off-email' && (
                <>
                    {!previewUrl && (
                        <Paper fullWidth sx={{ height: 'calc(100vh - 200px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }} >
                            <Box sx={{ display: 'flex', alignItems: 'center' }} >
                                <CircularProgress size={18} />&nbsp;
                                Loading preview...
                            </Box>
                        </Paper>
                    )}
                    {previewUrl && (
                        <Paper sx={{ height: 'calc(100vh - 200px)', width: '100%', padding: '3px' }}>
                            <iframe title='preview' src={`${getBaseUrl()}/offers?temporaryAccesstoken=${previewUrl}&type=preview`} style={{ height: 'calc(100vh - 206px)', width: '100%', border: '0px' }} />
                        </Paper>
                    )}
                </>
            )}
            <Dialog open={!!screen} onClose={() => setScreen(null)} maxWidth='none'>
                <InnerDialog 
                    org={org}
                    accesstoken={accesstoken} 
                    emailLoginInfo={emailLoginInfo}
                    onEmailRefreshConnection={onEmailRefreshConnection}
                    onEmailLogout={onEmailLogout}
                    screen={screen} 
                    setScreen={setScreen} 
                    organization={organization} 
                    setMessage={setMessage} 
                    opportunity={opportunity} 
                    accounts={accounts} 
                    onNewOpportunity={onNewOpportunity}
                    emailBody={emailBody}
                    minimized={minimized}
                    attachPDF={attachPDF}
                    setAttachPDF={setAttachPDF}
                />
            </Dialog>
            <CWXml
                initialStructure={cargowiseHistory?.structure}
                organization={organization}
                opportunity={opportunity}
                account={accounts.find(a => a.id === opportunity.account.id)} 
                me={me}
                accounts={accounts}
                accesstoken={accesstoken}
                suppliers={suppliers}
                open={exportCWDialogOpen}
                onClose={() => setExportCWDialogOpen(false)}
                onOk={onSendToCargoWise}
                seaPorts={seaPorts}
                unlocode={unlocode}
                exchangeRates={exchangeRates}
                lockedExchangeRates={lockedExchangeRates}
            />

            { message && (
                <Snackbar
                    open={message.open}
                    autoHideDuration={6000}
                    onClose={() => setMessage({ ...message, open: false })}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert
                        severity={message.severity}
                        variant='filled'
                        onClose={() => setMessage({ ...message, open: false })}
                        sx={{
                            borderRadius: '12px',
                            fontSize: '13px',
                            fontWeight: 500,
                            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                            minWidth: '320px',
                            alignItems: 'center',
                        }}
                    >
                        {message.message}
                    </Alert>
                </Snackbar>
            )}
            <QuotePdfNotice notice={pdfNotice} onClose={dismissPdfNotice} />
            <LockExchangeRatesDialog open={showLockWarning} onLock={() => onLockExchangeRates(true)} />
        </Box>
    );
}

// A field is "disconnected" when it sits in the UNASSIGNED holding bucket and no
// span in the preface references it — e.g. a template whose duplicate field ids
// collapsed in the loader's convertMap, leaving one copy wired to nothing.
function FinancialSummaryBar({ opportunity, exchangeRates, organization, lockedExchangeRates }) {
    if (!opportunity?.offers?.length || !exchangeRates || !organization) return null;

    const fraction = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const cur = organization.defaults?.currency || 'USD';
    const offers = opportunity.offers || [];

    // Use locked rates if available, otherwise live rates
    const hasLockedRates = lockedExchangeRates?.live && Object.keys(lockedExchangeRates.live).length > 0;
    const effectiveRates = hasLockedRates ? lockedExchangeRates.live : exchangeRates;
    const rateSource = hasLockedRates ? 'Locked' : 'Live';

    // Collect all currencies used across all offers
    const usedCurrencies = new Set();
    try {
        for (const offer of offers) {
            for (const section of offer.sections || []) {
                for (const off of section.offers || []) {
                    for (const field of off.fields || []) {
                        for (const [k, v] of Object.entries(field.values || {})) {
                            if (v?.currency) usedCurrencies.add(v.currency);
                        }
                    }
                }
            }
        }
        for (const br of opportunity.buyingRates || []) {
            for (const section of (br.offer?.sections || [])) {
                for (const off of section.offers || []) {
                    for (const field of off.fields || []) {
                        for (const [k, v] of Object.entries(field.values || {})) {
                            if (v?.currency) usedCurrencies.add(v.currency);
                        }
                    }
                }
            }
        }
    } catch (e) { /* ignore */ }
    usedCurrencies.delete(cur);

    const isSentWithoutLock = !hasLockedRates && opportunity.status && opportunity.status !== 'draft' && opportunity.status !== 'pre-draft';

    const rateLines = usedCurrencies.size > 0
        ? [...usedCurrencies].sort().map(c => {
            const rate = effectiveRates[cur] / effectiveRates[c];
            return `1 ${c} = ${rate ? rate.toFixed(4) : '?'} ${cur}`;
        })
        : [];

    // Detect display currencies set on offers, sections, and fields
    const displayCurrencies = new Set();
    for (const offer of offers) {
        const offerDc = offer.displayCurrency?.to || offer.displayCurrency;
        if (offerDc && offerDc.length === 3) displayCurrencies.add(offerDc);
        for (const section of offer.sections || []) {
            const dc = section.displayCurrency?.to || section.displayCurrency;
            if (dc && dc.length === 3) displayCurrencies.add(dc);
            for (const so of section.offers || []) {
                for (const field of so.fields || []) {
                    const fdc = field.displayCurrency?.to || field.displayCurrency;
                    if (fdc && fdc.length === 3) displayCurrencies.add(fdc);
                }
            }
        }
    }
    const hasDisplayCurrency = displayCurrencies.size > 0;
    const displayCurrencyList = [...displayCurrencies].join(', ');

    // Add display currency rate lines to tooltip
    const displayRateLines = hasDisplayCurrency ? [...displayCurrencies].map(dc => {
        const rate = effectiveRates[cur] / effectiveRates[dc];
        return rate ? `1 ${dc} = ${rate.toFixed(4)} ${cur}` : null;
    }).filter(Boolean) : [];

    const rateTooltip = [
        `All values shown in ${cur} (workspace default currency).`,
        hasDisplayCurrency
            ? hasLockedRates
                ? `Prices displayed in ${displayCurrencyList} · Rates locked on ${dayjs(lockedExchangeRates.locked).format('MMM D, YYYY [at] h:mm A')}.`
                : isSentWithoutLock
                    ? `Prices displayed in ${displayCurrencyList} · Rates were not locked when sent.`
                    : `Prices displayed in ${displayCurrencyList} using today's exchange rates. Rates will lock automatically when sent.`
            : hasLockedRates
                ? `Exchange rates locked on ${dayjs(lockedExchangeRates.locked).format('MMM D, YYYY [at] h:mm A')}.`
                : isSentWithoutLock
                    ? `Currency rates may have changed since this quote was sent. The profit shown is based on today's exchange rates, not the rates at the time of sending.`
                    : usedCurrencies.size > 0
                        ? `Using today's exchange rates. Rates will lock automatically when sent.`
                        : '',
        ...rateLines,
        ...displayRateLines.filter(l => !rateLines.includes(l)),
    ].filter(Boolean).join('\n');

    // Calculate financials per offer
    const offerRows = offers.map((offer, idx) => {
        // Shared with the opportunities table, so a quote's profit reads the same in both.
        const { sell, cost, gp } = calcOfferSellAndCost({ offer, opportunity, exchangeRates, organization });
        const margin = sell > 0 ? (gp / sell) * 100 : 0;
        const title = offer.title || `Offer ${idx + 1}`;
        return { title, sell, cost, gp, margin, isEmpty: sell === 0 && cost === 0 };
    }).filter(r => !r.isEmpty);

    if (offerRows.length === 0) return null;

    return (
        <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{rateTooltip}</span>} placement='bottom' arrow>
            <Box sx={{
                display: 'flex', flexDirection: 'column', gap: '2px',
                backgroundColor: '#f8fafc',
                borderBottom: '1px solid #e2e8f0',
                cursor: 'help',
            }}>
                {offerRows.map((row, rowIdx) => (
                    <Box key={rowIdx} sx={{ display: 'flex', gap: '16px', px: '24px', py: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                        {offerRows.length > 1 && (
                            <Typography sx={{ fontSize: '11px', fontWeight: 600, color: 'text.primary', minWidth: '80px' }}>
                                {row.title}
                            </Typography>
                        )}
                        {[
                            { label: 'Sell', value: currencyToText(cur, true, fraction.format(row.sell)), color: 'text.primary' },
                            { label: 'Cost', value: currencyToText(cur, true, fraction.format(row.cost)), color: 'text.secondary' },
                            { label: 'GP', value: currencyToText(cur, true, fraction.format(row.gp)), color: row.gp >= 0 ? green[700] : red[500] },
                            { label: 'Margin', value: `${fraction.format(row.margin)}%`, color: row.gp >= 0 ? green[700] : red[500] },
                        ].map((item, i, arr) => (
                            <Box key={item.label} sx={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                                <Typography sx={{ fontSize: '11px', color: 'text.secondary', fontWeight: 500 }}>{item.label}:</Typography>
                                <Typography sx={{ fontSize: '13px', fontWeight: 700, color: item.color }}>{item.value}</Typography>
                                {i < arr.length - 1 && <Typography sx={{ fontSize: '11px', color: '#cbd5e1', ml: '10px' }}>|</Typography>}
                            </Box>
                        ))}
                    </Box>
                ))}
            </Box>
        </Tooltip>
    );
}

export default OpportunityPreviewAndSendBase;
export { StatusMenu, FinancialSummaryBar };

function LockExchangeRatesDialog(props) {
    const { open, onLock } = props;
    const [ closed, setClosed ] = useState(false);
    const [ locking, setLocking ] = useState(false);

    return (
        <Dialog open={open && !closed} onClose={() => {}}>
            <DialogTitle>
                <FontAwesomeIcon icon={faTriangleExclamation} /> Lock Exchange Rates
            </DialogTitle>
            <DialogContent>
                Before sending your offer, it is <i><b>highly recommended</b></i> to lock the exchange rates so the offer won't change in the future.
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setClosed(true)} variant='outlined' color='error' disabled={locking}>
                    Continue without locking
                </Button>
                <Button onClick={() => {
                    setLocking(true);
                    onLock();
                }} variant='contained' color='primary' disabled={locking}>
                    {locking ? <CircularProgress size={18} /> : 'Lock Exchange Rates'}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

function InnerDialog(props) {
    const { accesstoken, screen, setScreen, organization, setMessage,
         opportunity, accounts, onNewOpportunity, emailBody, 
         emailLoginInfo, onEmailRefreshConnection, onEmailLogout, minimized,
         attachPDF, setAttachPDF,
    } = props;

    async function onCopyLink({ email }) {
        try {
            setScreen('generating-link');

            const { data: postedOpportunity } = await axios.put(`/api/opportunities/${opportunity.id}?organizationId=${organization.id}&accesstoken=${accesstoken}`, {
                opportunity: {
                    status: 'sent-to-endcustomer',
                    account: opportunity.account,
                }
            });

            const { data: url } = await axios(`/api/opportunities/${postedOpportunity.id}/offer-url?organizationId=${organization.id}&accesstoken=${accesstoken}`);

            await navigator.clipboard.writeText(`${getBaseUrl()}/offers?temporaryAccesstoken=${url}`);

            setScreen('preview-enduser');
            setMessage({ open: true, severity: 'success', message: 'Link copied to clipboard.' });
        } catch (e) {
            setScreen('preview-enduser');
            setMessage({ open: true, severity: 'error', message: 'Error generating link - did you enter a valid email and organization?' });
        }
    }

    // Poll for email completion and update opportunity status
    async function pollForEmailCompletion({ emailId, toRecipients, ccRecipients }) {
        try {
            const pollResult = await email.waitForEmailSent({
                org: organization,
                emailId,
                monadaAccesstoken: accesstoken
            });

            if (pollResult.success) {
                // Email sent successfully, update opportunity status
                const { data: postedParentOpportunity } = await axios.put(`/api/opportunities/${opportunity.id}?organizationId=${organization.id}&accesstoken=${accesstoken}`, {
                    opportunity: {
                        status: 'sent-to-endcustomer',
                        account: opportunity.account,
                        pendingEmailId: null,
                    },
                    reason: JSON.stringify({
                        source: 'email-sent',
                        toRecipients,
                        ccRecipients
                    })
                });
                onNewOpportunity({ opportunity: { ...opportunity, ...postedParentOpportunity } });
                setMessage({ open: true, severity: 'success', message: `Quote sent successfully to ${toRecipients[0]?.address || 'recipient'}` });
            } else {
                setMessage({ open: true, severity: 'error', message: `Failed to send email: ${pollResult.error || 'Unknown error'}` });
            }
        } catch (e) {
            setMessage({ open: true, severity: 'error', message: `Error sending quote: ${e.toString()}` });
        }
    }

    async function onSentEndCustomer(emails) {
        // Check if this is a background send (user clicked "Continue in background...")
        if (emails[0]?.queued) {
            const emailId = emails[0].emailId;
            const toRecipients = emails[0].toRecipients || [];
            const ccRecipients = emails[0].ccRecipients || [];

            try {
                // Update opportunity status to 'sending-to-endcustomer' with email info
                const { data: postedParentOpportunity } = await axios.put(`/api/opportunities/${opportunity.id}?organizationId=${organization.id}&accesstoken=${accesstoken}`, {
                    opportunity: {
                        status: 'sending-to-endcustomer',
                        account: opportunity.account,
                        pendingEmailId: emailId,
                    },
                    reason: JSON.stringify({
                        source: 'email-queued',
                        toRecipients,
                        ccRecipients,
                        emailId
                    })
                });
                onNewOpportunity({ opportunity: { ...opportunity, ...postedParentOpportunity } });

                // Poll for email status in the background
                pollForEmailCompletion({
                    emailId,
                    toRecipients,
                    ccRecipients
                });
            } catch (e) {
                setMessage({ open: true, severity: 'error', message: `Error updating opportunity: ${e.toString()}` });
            }

            return;
        }

        try {
            const { data: postedParentOpportunity } = await axios.put(`/api/opportunities/${opportunity.id}?organizationId=${organization.id}&accesstoken=${accesstoken}`, {
                opportunity: {
                    status: 'sent-to-endcustomer',
                    account: opportunity.account,
                },
                reason: JSON.stringify({
                    source: 'email-sent',
                    toRecipients: emails.map(e => e.toRecipients).flat(),
                    ccRecipients: emails.map(e => e.ccRecipients).flat()
                })
            });
            onNewOpportunity({ opportunity: { ...opportunity, ...postedParentOpportunity } });
            setMessage({ open: true, severity: 'success', message: `Quote sent successfully to ${emails[0].toRecipients[0].address}` });
        } catch (e) {
            setMessage({ open: true, severity: 'error', message: `Error sending quote ${e.toString()}` });
            setScreen('preview-endcustomer');
        }
    }

    if (screen === 'preview-link') {
        return (
            <Box sx={{ padding: '0px 24px' }}>
                <LinkDialog
                    opportunity={opportunity}
                    accesstoken={accesstoken}
                    onBack={() => setScreen(null)}
                    onCopyLink={onCopyLink}
                    onNewOpportunity={onNewOpportunity}
                    accountOrg={accounts.find(a => a.id === opportunity.account.id).name}
                    organizationId={organization.id}
                />
            </Box>
        );
    }

    if ((screen === 'preview-enduser') && (opportunity.source?.type !== 'email')) {
        const account = accounts.find(a => a.id === opportunity.account.id);
        let toRecipients = [];
        let ccRecipients = [];

        const lastSentLog = _.findLast(opportunity.log, l => l.action === 'sent-to-endcustomer');
        if (lastSentLog && lastSentLog.reason) {
            const reason = JSON.parse(lastSentLog.reason);
            ccRecipients = reason.ccRecipients || [];
            toRecipients = reason.toRecipients || [];
        } else {
            toRecipients = [...(account.contacts || []).map(c => ({ address: c.email, name: _.trim(`${c.firstName || ''} ${c.lastName || ''}`) }))];
        }

        return (
            <Box sx={{ padding: '0px 24px' }}>
                <SendOpportunityDialog
                    org={organization}
                    accesstoken={accesstoken}
                    accounts={accounts}
                    emailLoginInfo={emailLoginInfo}
                    onEmailRefreshConnection={onEmailRefreshConnection}
                    onEmailLogout={onEmailLogout}
                    opportunity={opportunity}
                    toRecipients={toRecipients?.[0]?.address ? toRecipients : []}
                    ccRecipients={ccRecipients?.[0]?.address ? ccRecipients : []}
                    initialBody={emailBody}
                    minimized={minimized}
                    onClose={() => setScreen(null)}
                    onSent={onSentEndCustomer}
                    attachPDF={attachPDF}
                    setAttachPDF={setAttachPDF}
                />
            </Box>
        );
    }

    if ((screen === 'preview-enduser') && (opportunity.source?.type === 'email')) {
        return (
            <Box sx={{ padding: '0px 24px' }}>
                <SendOpportunityReplyDialog
                    opportunity={opportunity}
                    org={organization}
                    accesstoken={accesstoken}
                    emailLoginInfo={emailLoginInfo}
                    accounts={accounts}
                    onEmailRefreshConnection={onEmailRefreshConnection}
                    onEmailLogout={onEmailLogout}
                    conversationId={opportunity.source.id}
                    initialBody={emailBody}
                    onSent={onSentEndCustomer}
                    minimized={minimized}
                    onClose={() => setScreen(null)}
                    attachPDF={attachPDF}
                    setAttachPDF={setAttachPDF}
                />
            </Box>
        );
    }

    if (screen === 'generating-link') {
        return (
            <div>
                <Box style={{ padding: '12px' }}>
                    <Typography variant='h6'>
                        Generating Link...
                    </Typography>
                    <Typography variant='body2' sx={{ paddingTop: '12px' }}>
                        We're generating a trackable link you can send to your customer...
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <CircularProgress sx={{ marginTop: '24px' }} />
                    </Box>
                </Box>
            </div>
        );
    }

    if (screen === 'preview-sending') {
        return (
            <div>
                <Box style={{ padding: '12px' }}>
                    <Typography variant='h6'>
                        Sending email...
                    </Typography>
                    <Typography variant='body2' sx={{ paddingTop: '12px' }}>
                        We're generating and sending the quote to the customer...
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <CircularProgress sx={{ marginTop: '24px' }} />
                    </Box>
                </Box>
            </div>
        );
    }
}

function LinkDialog(props) {
    const { opportunity, accesstoken, organizationId, onBack, onNewOpportunity } = props;

    const [ link, setLink ] = useState(null);
    const [ error, setError ] = useState(false);
    const [ waiting, setWaiting ] = useState(false);
    const [ waitingStart, setWaitingStart ] = useState(true);

    useEffect(() => {
        (async function() {
            if (opportunity.status) {
                const { data: url } = await axios(`/api/opportunities/${opportunity.id}/offer-url?organizationId=${organizationId}&accesstoken=${accesstoken}`);
                setLink(`${getBaseUrl()}/offers?temporaryAccesstoken=${url}`);
                setWaitingStart(false);
            } else {
                setWaitingStart(false);
            }
        })()
    }, []);

    async function onCopyLink() {
        setWaiting(true);
        setError(false);
        setLink(null);

        try {
            const { data: postedOpportunity } = await axios.put(`/api/opportunities/${opportunity.id}?organizationId=${organizationId}&accesstoken=${accesstoken}`, {
                opportunity: {
                    status: 'sent-to-endcustomer',
                }
            });

            const { data: url } = await axios(`/api/opportunities/${postedOpportunity.id}/offer-url?organizationId=${organizationId}&accesstoken=${accesstoken}`);

            setLink(`${getBaseUrl()}/offers?temporaryAccesstoken=${url}`);

            onNewOpportunity({ opportunity: {...opportunity, ...postedOpportunity }});
            setWaiting(false);

        } catch(e) {
            setWaiting(false);
            setError(true);
        }
    }

    if (waitingStart) {
        return (
            <Grid container spacing={0} style={{ padding: '24px' }}>
                <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '24px' }}>
                    <Typography variant='h6'>
                        Generate Link
                    </Typography>
                    <Button
                        variant='text'
                        color='secondary'
                        onClick={onBack}
                    >
                        <FontAwesomeIcon icon={faTimes} />
                    </Button>
                </Grid>
                <Grid item xs={12}>
                    <Box sx={{ paddingBottom: '24px', display: 'flex', alignItems: 'center', textAlign: 'center', justifyContent: 'center' }}>
                        <CircularProgress />
                    </Box>
                </Grid>
            </Grid>
        )
    }

    return (
        <Box sx={{ p: 4, maxWidth: '480px' }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ width: 36, height: 36, borderRadius: '10px', background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <FontAwesomeIcon icon={faLink} style={{ color: '#fff', fontSize: '15px' }} />
                    </Box>
                    <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#0f172a' }}>Share Link</Typography>
                </Box>
                <IconButton size='small' onClick={onBack} sx={{ color: '#94a3b8', '&:hover': { color: '#64748b' } }}>
                    <FontAwesomeIcon icon={faTimes} />
                </IconButton>
            </Box>

            {/* Description */}
            <Typography sx={{ fontSize: '14px', color: '#475569', lineHeight: 1.6, mb: 1.5 }}>
                Share your proposal with a link you can send via email, WhatsApp, or any channel.
            </Typography>
            <Typography sx={{ fontSize: '12px', color: '#94a3b8', lineHeight: 1.5, mb: 3 }}>
                Each link is tied to an email & company. When a client accepts, it's logged automatically.
                If this quote was reopened and revised, previously shared links will redirect to the latest version — no need to resend.
            </Typography>

            {/* Link generated */}
            {link && (
                <Box sx={{ border: '1px solid #d1fae5', borderRadius: '12px', background: '#ecfdf5', p: 2.5, mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                        <Box sx={{ width: 28, height: 28, borderRadius: '50%', background: '#10b981', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <FontAwesomeIcon icon={faCheck} style={{ color: '#fff', fontSize: '13px' }} />
                        </Box>
                        <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#065f46' }}>Link Generated</Typography>
                    </Box>
                    <Box sx={{ p: 1.5, background: '#fff', border: '1px solid #d1fae5', borderRadius: '8px', mb: 1.5, wordBreak: 'break-all' }}>
                        <Typography sx={{ fontSize: '11px', color: '#64748b', fontFamily: 'monospace', lineHeight: 1.5 }}>{link}</Typography>
                    </Box>
                    <CopyToClipboardButton buttonProps={{ variant: 'contained', size: 'small', sx: { textTransform: 'none', borderRadius: '8px', fontSize: '13px', background: '#10b981', '&:hover': { background: '#059669' } } }} link={link} />
                </Box>
            )}

            {/* Generate button */}
            {!link && (
                <Box sx={{ textAlign: 'right' }}>
                    <LoadingButton
                        loading={waiting}
                        onClick={onCopyLink}
                        variant='contained'
                        sx={{ textTransform: 'none', borderRadius: '10px', px: 3, py: 1, fontSize: '14px', fontWeight: 600, background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', '&:hover': { background: 'linear-gradient(135deg, #4f46e5, #7c3aed)' } }}
                    >
                        Generate Link
                    </LoadingButton>
                </Box>
            )}

            {/* Error */}
            {error && (
                <Alert severity='error' sx={{ mt: 2, borderRadius: '10px' }}>
                    Error generating link. Please try again later.
                </Alert>
            )}
        </Box>
    )
}

function CopyToClipboardButton(props) {
    const { link, buttonProps } = props;
    const [ error, setError ] = useState(false);
    const [ success, setSuccess ] = useState(false);
    const [ working, setWorking ] = useState(false);

    async function onCopy() {
        setWorking(true);
        setSuccess(false);
        setError(false);
        try {
            await navigator.clipboard.writeText(link);
            setWorking(false);
            setSuccess(true);
            await new Promise(res => setTimeout(res, 3000));
            setSuccess(false);
        } catch(e) {
            setWorking(false);
            setError(true);
            await new Promise(res => setTimeout(res, 3000));
            setError(false);
        }
    }

    if (success) {
        return (
            <Box sx={{ width: '120px', height: '50px', fontSize: '13px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <FontAwesomeIcon icon={faClipboard} style={{ marginRight: '20px' }}/>
                <span style={{ marginRight: '28px', fontWeight: '500', fontSize: '11px' }}>COPIED!</span>
            </Box>
        )
    }

    if (error) {
        return <span>Error!</span>;
    }

    return (
        <LoadingButton loading={working} {...buttonProps} onClick={onCopy}>
            <FontAwesomeIcon icon={faClipboard} />
            <span style={{ fontSize: '11px' }}>
                Copy to clipboard
            </span>
        </LoadingButton>
    )

}

function StatusMenu(props) {
    const { opportunity, disabled, setMessage, organization, accesstoken, onNewOpportunity } = props;
    const [anchorEl, setAnchorEl] = useState(null);
    const [ working, setWorking ] = useState(false);
    const [ pickOffers, setPickOffers ] = useState(false);
    const [ showAcceptDialog, setShowAcceptDialog ] = useState(false);
    const [ askLostReason, setAskLostReason ] = useState(false);
    const open = Boolean(anchorEl);
    const theme = useTheme();

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    async function onSetStatus({ status, selectedOffer, lostReason }) {
        try {
            const { data: posted } = await axios.put(`/api/opportunities/${opportunity.id}?organizationId=${organization.id}&accesstoken=${accesstoken}`, {
                opportunity: { status, ...(selectedOffer ? { selected: [ selectedOffer ]} : {}) },
                ...(lostReason ? { lostReason: _.omit(lostReason, 'offerId') } : {}),
                ...(lostReason?.offerId ? { lostOffer: lostReason.offerId } : {}),
            });

            onNewOpportunity({ opportunity: { ...opportunity, ...posted } });
            setMessage({ open: true, severity: 'success', message: `Quote status changed successfully` });
        } catch (e) {
            setMessage({ open: true, severity: 'error', message: `Error changing quote status ${e.toString()}` });
        }
    }

    async function onMarkWon() {
        handleClose();
        setWorking(true);
        if (opportunity.offers?.length > 1) {
            setPickOffers(true);
        } else {
            await onSetStatus({ status: 'completed' });
            setWorking(false);
        }
    }

    function onMarkLost() {
        handleClose();
        setAskLostReason(true);
    }

    async function onConfirmLost(lostReason) {
        setWorking(true);
        await onSetStatus({ status: 'lost', lostReason });
        setWorking(false);
        setAskLostReason(false);
    }

    async function onReopen() {
        handleClose();
        setWorking(true);
        await onSetStatus({ status: null });
        setWorking(false);
    }

    async function onMarkAccepted() {
        handleClose();
        setShowAcceptDialog(true);
    }

    return (
        <div>
            <Button
                disabled={disabled || working}
                variant='text'
                color='primary'
                sx={{ 
                    margin: '0px',
                    minWidth: '0px', 
                    height: 'auto', 
                    padding: '0px',
                    fontSize: '11px !important',
                    fontWeight: 400,
                    textTransform: 'none', 
                    position: 'relative',
                    color: theme.palette.text.secondary,
                    '& *': {
                        fontSize: '11px !important'
                    }
                }}
                onClick={handleClick}
            >
                {working && (
                    <Box sx={{ 
                        position: 'absolute', 
                        right: '0px', 
                        top: '0px', 
                        width: '100%', 
                        height: '100%', 
                        backgroundColor: alpha(theme.palette.background.default, 0.5), 
                        zIndex: 1, 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center' 
                    }}>
                        <CircularProgress size={14} />
                    </Box>
                )}
                Opportunity Status <span style={{ fontWeight: 600, marginLeft: '4px' }}>{statusToText({ opportunity })}</span> <FontAwesomeIcon icon={faChevronDown} style={{ marginLeft: '4px', fontSize: '11px' }} />
            </Button>
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
            >
                <MenuItem onClick={onMarkAccepted} disabled={opportunity.status !== null && opportunity.status !== 'sent-to-endcustomer'}>Mark as Accepted</MenuItem>
                <MenuItem onClick={onMarkWon} disabled={opportunity.status === 'completed'}>Mark as Won</MenuItem>
                <MenuItem onClick={onMarkLost} disabled={opportunity.status === 'lost'}>Mark as Lost</MenuItem>
                <MenuItem onClick={onReopen} disabled={!opportunity.status}>Reopen</MenuItem>
            </Menu>
            <LostReasonDialog
                open={askLostReason}
                working={working}
                onClose={() => setAskLostReason(false)}
                onConfirm={onConfirmLost}
                offers={opportunity.offers}
            />
            {pickOffers && (
                <PickOffersAndMarkWonDialog 
                    opportunity={opportunity} 
                    defaultOffer={opportunity.offers.find(o => opportunity.selected?.includes(o.id))?.id}
                    onNewOpportunity={(props) => {
                        setWorking(false);
                        setMessage({ open: true, severity: 'success', message: `Quote status changed successfully` });
                        onNewOpportunity(props);
                    }}
                    organizationId={organization.id}
                    accesstoken={accesstoken} 
                    onError={(e) => {
                        setMessage({ open: true, severity: 'error', message: `Error changing quote status ${e.toString()}` });
                    }} 
                    onClose={() => setPickOffers(false)}
                />
            )}
            {showAcceptDialog && (
                <MarkAsAcceptedDialog
                    opportunity={opportunity}
                    onNewOpportunity={(props) => {
                        setWorking(false);
                        setMessage({ open: true, severity: 'success', message: `Quote status changed successfully` });
                        onNewOpportunity(props);
                    }}
                    organizationId={organization.id}
                    accesstoken={accesstoken}
                    onError={(e) => {
                        setWorking(false);
                        setMessage({ open: true, severity: 'error', message: `Error changing quote status ${e.toString()}` });
                    }}
                    onClose={() => {
                        setShowAcceptDialog(false);
                        setWorking(false);
                    }}
                />
            )}
        </div>
    );
}

// Dummy values for skipped contact info
export const DUMMY_CUSTOMER_NAME = '[skipped]';
export const DUMMY_CUSTOMER_EMAIL = 'skipped@email.com';

function MarkAsAcceptedDialog(props) {
    const { opportunity, onClose, onNewOpportunity, organizationId, accesstoken, onError } = props;
    const [ customerName, setCustomerName ] = useState('');
    const [ customerEmail, setCustomerEmail ] = useState('');
    const [ emailError, setEmailError ] = useState('');
    const [ working, setWorking ] = useState(false);
    const [ skip, setSkip ] = useState(false);

    async function onAccept(opts={}) {
        let trimmedEmail, trimmedName;
        if (opts.skip) {
            setSkip(true);
            trimmedName = DUMMY_CUSTOMER_NAME;
            trimmedEmail = DUMMY_CUSTOMER_EMAIL;
        } else {
            trimmedEmail = customerEmail.trim();
            trimmedName = customerName.trim();
        }

        if (!trimmedName || !trimmedEmail) {
            if (opts.skip) setSkip(false);
            return;
        }

        if (!opts.skip && !validator.validate(trimmedEmail)) {
            setEmailError('Please enter a valid email address');
            return;
        }

        setEmailError('');
        if (opts.skip) {
            setSkip(true);
        } else {
            setWorking(true);
        }

        try {
            const { data: posted } = await axios.put(`/api/opportunities/${opportunity.id}?organizationId=${organizationId}&accesstoken=${accesstoken}`, {
                opportunity: { status: 'endcustomer-accepted' },
                name: trimmedName,
                email: trimmedEmail,
            });

            onNewOpportunity({ opportunity: { ...opportunity, ...posted } });
            setWorking(false);
            setSkip(false);
            onClose();
        } catch (e) {
            setWorking(false);
            setSkip(false);
            onError(e);
        }
    }

    return (
        <Dialog open={true} onClose={onClose} PaperProps={{ sx: { borderRadius: '16px', overflow: 'hidden', maxWidth: '440px' } }}>
            <Box sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                    <Box sx={{ width: 36, height: 36, borderRadius: '10px', background: '#ecfdf5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <FontAwesomeIcon icon={faCheck} style={{ color: '#10b981', fontSize: '16px' }} />
                    </Box>
                    <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#0f172a' }}>Mark as Accepted</Typography>
                </Box>
                <Typography sx={{ fontSize: '14px', color: '#64748b', lineHeight: 1.6, mb: 3 }}>
                    Who accepted this offer? This helps track deal attribution and customer engagement.
                </Typography>
                <TextField
                    fullWidth
                    label='Contact Name'
                    variant='outlined'
                    size='small'
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    required
                    sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                    disabled={working || skip}
                />
                <TextField
                    fullWidth
                    label='Contact Email'
                    type='email'
                    variant='outlined'
                    size='small'
                    value={customerEmail}
                    onChange={(e) => {
                        setCustomerEmail(e.target.value);
                        if (emailError && validator.validate(e.target.value.trim())) {
                            setEmailError('');
                        }
                    }}
                    error={!!emailError}
                    helperText={emailError}
                    required
                    disabled={working || skip}
                    sx={{ mb: 3, '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
                    <Button onClick={onClose} disabled={working || skip} sx={{ textTransform: 'none', borderRadius: '10px', color: '#64748b' }}>
                        Cancel
                    </Button>
                    <Button
                        onClick={() => onAccept({ skip: true })}
                        variant='text'
                        disabled={working || skip}
                        sx={{ textTransform: 'none', borderRadius: '10px', color: '#94a3b8', position: 'relative', minWidth: '70px' }}
                    >
                        {skip ? <CircularProgress size={20} sx={{ position: 'absolute', left: 'calc(50% - 10px)' }} /> : "Skip"}
                    </Button>
                    <LoadingButton
                        loading={working}
                        onClick={() => onAccept()}
                        variant='contained'
                        disabled={!customerName.trim() || !customerEmail.trim() || working || skip}
                        sx={{ textTransform: 'none', borderRadius: '10px', px: 3, background: '#10b981', '&:hover': { background: '#059669' } }}
                    >
                        Mark as Accepted
                    </LoadingButton>
                </Box>
            </Box>
        </Dialog>
    );
}